import debounce from 'lodash/debounce';

class FacetFilters extends HTMLElement {
    constructor() {
        super();

        this.body = document.querySelector('body');

        this.mobileFiltersToggle = this.querySelectorAll('button[data-filters-mobile-toggle]')
        this.mobileFiltersMenu = this.querySelector('div[data-filters-popup-mobile]');
        this.mobileFiltersAccordionsToggle = this.querySelectorAll('button[data-facet-accordion-toggle]');
        this.mobileFiltersModalBg = this.querySelector('.modal-overlay');

        this.addEventListeners();
        this.bindEvents();
    }

    addEventListeners () {
        this.onMobileFiltersToggle();
        this.onModalBgClick();
    }

    bindEvents () {
        this.querySelectorAll('button[data-desktop-filter]').forEach(filter => filter.removeEventListener('click', this.onDesktopFiltersClick))
        this.querySelectorAll('button[data-facet-accordion-toggle]').forEach(accordion => accordion.removeEventListener('click', this.onMobileAccordionsClick));
        this.querySelectorAll('button[data-desktop-filter]').forEach(filter => filter.addEventListener('click', () => this.onDesktopFiltersClick(filter)))
        this.querySelectorAll('button[data-facet-accordion-toggle]').forEach(accordion => accordion.addEventListener('click', this.onMobileAccordionsClick.bind(accordion)));
    }

    onDesktopFiltersClick (filter) {
        const chevron = filter.querySelector('.js-filter-caret');
        const popup = filter.nextElementSibling;
        if (popup.classList.contains('invisible')) {
            this.closeAllFilterPopups();
            popup.classList.remove('invisible');
            popup.classList.remove('opacity-0');
            popup.classList.remove('h-0');
            chevron.classList.add('rotate-180');
        } else {
            popup.classList.add('invisible');
            popup.classList.add('opacity-0');
            popup.classList.add('h-0');
            chevron.classList.remove('rotate-180');
        }
    }

    closeAllFilterPopups () {
        this.querySelectorAll('div[data-desktop-filter]').forEach(filter => {
            const chevron = filter.querySelector('.js-filter-caret');
            const popup = filter.nextElementSibling;

            if (!popup.classList.contains('invisible')) {
                popup.classList.add('invisible', 'opacity-0', 'h-0');
                chevron.classList.remove('rotate-180');
            }
        })
    }

    onMobileFiltersToggle () {
        this.mobileFiltersToggle.forEach(toggle => {
            toggle.addEventListener('click', () => {
                this.classList.toggle('is-active');
                this.mobileFiltersMenu.classList.toggle('-translate-x-full');
                this.toggleBodyOverflow();
            })
        });
    }

    onModalBgClick() {
        this.mobileFiltersModalBg.addEventListener("click", () => {
            this.classList.toggle('is-active');
            this.mobileFiltersMenu.classList.toggle('-translate-x-full');
            this.toggleBodyOverflow();
        })
    }

    onMobileAccordionsClick (accordion) {
        const button = accordion.currentTarget;
        const content = accordion.currentTarget.nextElementSibling;
        const arrow = accordion.currentTarget.querySelector('[data-facet-accordion-arrow]');

        button.classList.toggle('is-active');

        arrow?.classList.toggle('is-active');

        content.classList.toggle('h-0');
        content.classList.toggle('hidden');
    }

    toggleBodyOverflow () {
        if (this.mobileFiltersMenu.classList.contains('-translate-x-full')) {
            this.body.classList.remove('overflow-hidden');
        } else {
            this.body.classList.add('overflow-hidden');
        }
    }
}

class FacetFiltersForm extends HTMLElement {
    constructor() {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
        this.delay = null;

        this.debouncedOnSubmit = debounce((event) => {
            this.onSubmitHandler(event);
        }, 500);

        this.querySelector('form').addEventListener('input', (event) => {
            // Adds a delay to ensure user has enough time to adjust inputs
            if (event.target.classList.contains('js-facet-filters-delay')) {
                clearTimeout(this.delay);
                this.delay = setTimeout(() => {
                    this.debouncedOnSubmit(event);
                }, 1500);
            } else {
                this.debouncedOnSubmit(event);
            }
        });

        const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
        if (facetWrapper) facetWrapper.addEventListener('keyup', FacetFiltersForm.onKeyUpEscape);
    }

    static onKeyUpEscape(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        const openDetailsElement = event.target.closest('details[open]');
        if (!openDetailsElement) return;

        const summaryElement = openDetailsElement.querySelector('summary');
        openDetailsElement.removeAttribute('open');
        summaryElement.setAttribute('aria-expanded', false);
        summaryElement.focus();
    }

    static setListeners() {
        const onHistoryChange = (event) => {
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) return;
            FacetFiltersForm.renderPage(searchParams, null, false);
        }
        window.addEventListener('popstate', onHistoryChange);
    }

    static toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element) => {
            element.classList.toggle('disabled', disable);
        });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();

        document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

        sections.forEach((section) => {
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = element => element.url === url;

            FacetFiltersForm.filterData.some(filterDataUrl) ?
                FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
                FacetFiltersForm.renderSectionFromFetch(url, event);
        });

        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    }

    static renderSectionFromFetch(url, event) {
        fetch(url)
            .then(response => response.text())
            .then((responseText) => {
                const html = responseText;
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
                FacetFiltersForm.renderFilters(html, event);
                FacetFiltersForm.renderProductGridContainer(html);
                FacetFiltersForm.renderProductCount(html);
            });
    }

    static renderSectionFromCache(filterDataUrl, event) {
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
    }

    static renderProductGridContainer(html) {
        document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
    }

    static renderProductCount(html) {
        const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount')?.innerHTML
        const countElement = document.getElementById('ProductCount');

        if (count) countElement.innerHTML = count;
    }

    static renderFilters(html) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

        const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

        facetDetailsElements.forEach((element) => {
            document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
        });

        FacetFiltersForm.renderAdditionalElements(parsedHTML);
    }

    static renderAdditionalElements(html) {
        const mobileElementSelectors = ['.js-mobile-facets__count'];

        mobileElementSelectors.forEach((selector) => {
            if (!html.querySelector(selector)) return;
            document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
        });

        document.getElementById('FacetFiltersFormMobile').closest('facet-filters').bindEvents();
    }

    static updateURLHash(searchParams) {
        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    static getSections() {
        return [
            {
                section: document.getElementById('product-grid').dataset.id,
            }
        ]
    }

    createSearchParams(form) {
        const formData = new FormData(form);

        /* Excludes default values from appending to query string */
        const sortElementWithDefault = form.querySelector('[name="sort_by"].js-filter-sort-default');
        const priceRangeFromElement = form.querySelector('[name="filter.v.price.gte"]');
        const priceRangeToElement = form.querySelector('[name="filter.v.price.lte"]');

        if (sortElementWithDefault && sortElementWithDefault.value === formData.get('sort_by')) {
            formData.delete('sort_by');
        }

        if (priceRangeFromElement && Number.parseFloat(priceRangeFromElement.value).toFixed(2) === Number.parseFloat(priceRangeFromElement.min).toFixed(2)) {
            formData.delete('filter.v.price.gte');
        }

        if (priceRangeToElement && Number.parseFloat(priceRangeToElement.value).toFixed(2) === Number.parseFloat(priceRangeToElement.max).toFixed(2)) {
            formData.delete('filter.v.price.lte');
        }

        return new URLSearchParams(formData).toString();
    }

    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
        window.scrollBy(document.getElementById('collection-layout').getBoundingClientRect().left, document.getElementById('collection-layout').getBoundingClientRect().top);
    }

    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');
        if (event.srcElement.className == 'mobile-facets__checkbox') {
            const searchParams = this.createSearchParams(event.target.closest('form'))
            this.onSubmitForm(searchParams, event)
        } else {
            const forms = [];
            const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

            sortFilterForms.forEach((form) => {
                if (!isMobile) {
                    if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
                        forms.push(this.createSearchParams(form));
                    }
                } else if (form.id === 'FacetFiltersFormMobile') {
                    forms.push(this.createSearchParams(form));
                }
            });
            this.onSubmitForm(forms.join('&'), event)
        }
    }

    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url);
        window.scrollBy(document.getElementById('collection-layout').getBoundingClientRect().left, document.getElementById('collection-layout').getBoundingClientRect().top);
    }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
    constructor() {
        super();
        this.activeArea = this.querySelector('.js-price-range-active-area');
        this.fromFakeInput = this.querySelector('.js-price-range-from-fake-input');
        this.toFakeInput = this.querySelector('.js-price-range-to-fake-input');
        this.fromInput = this.querySelector('.js-price-range-from-input');
        this.toInput = this.querySelector('.js-price-range-to-input');
        this.fromIndicator = this.querySelector('.js-price-range-from-indicator');
        this.toIndicator = this.querySelector('.js-price-range-to-indicator');

        this.fromFakeInput?.addEventListener('input', this.setFromFakeInput.bind(this, ...[this.fromFakeInput, this.toFakeInput, this.fromInput]));
        this.toFakeInput?.addEventListener('input', this.setToFakeInput.bind(this, ...[this.fromFakeInput, this.toFakeInput, this.toInput]));
        this.fromInput?.addEventListener('input', this.setFromInput.bind(this, ...[this.fromFakeInput, this.fromInput, this.toInput]));
        this.toInput?.addEventListener('input', this.setToInput.bind(this, ...[this.toFakeInput, this.fromInput, this.toInput]));

        this.setRangeChangeTimeout = null;
    }

    setFromFakeInput(fromFakeInput, toFakeInput, fromInput) {
        const from = parseFloat(fromFakeInput.value);
        const to = parseFloat(toFakeInput.value);

        this.setActiveAreaDecoration(fromFakeInput, toFakeInput);
        this.setPreciseIndicator(fromFakeInput, toFakeInput, this.fromIndicator);

        if (from > to) {
            fromFakeInput.value = to;
            fromInput.value = to;
        } else {
            fromInput.value = from;
        }
    }

    setToFakeInput(fromFakeInput, toFakeInput, toInput) {
        const from = parseFloat(fromFakeInput.value);
        const to = parseFloat(toFakeInput.value);

        this.setActiveAreaDecoration(fromFakeInput, toFakeInput);
        this.setPreciseIndicator(toFakeInput, fromFakeInput, this.toIndicator);

        if (from <= to) {
            toFakeInput.value = to;
            toInput.value = to;
        } else {
            toInput.value = from;
            toFakeInput.value = from;
        }
    }

    setFromInput(fromFakeInput, fromInput, toInput) {
        const from = parseFloat(fromInput.value);
        const to = parseFloat(toInput.value);

        if (from > to) {
            fromFakeInput.value = to;
            fromInput.value = to;
        } else {
            fromFakeInput.value = from;
        }
    }

    setToInput(toFakeInput, fromInput, toInput) {
        const from = parseFloat(fromInput.value);
        const to = parseFloat(toInput.value);

        if (from <= to) {
            toFakeInput.value = to;
            toInput.value = to;
        } else {
            toInput.value = from;
        }
    }

    setActiveAreaDecoration(from, to) {
        const range = to.max - to.min;
        const fromPosition = from.value - to.min;
        const toPosition = to.value - to.min;

        this.activeArea.style.backgroundImage = `linear-gradient(
            to right,
            var(--color-uiDarkGrey) 0%,
            var(--color-uiDarkGrey) ${(fromPosition)/(range)*100}%,
            var(--color-primary) ${((fromPosition)/(range))*100}%,
            var(--color-primary) ${(toPosition)/(range)*100}%,
            var(--color-uiDarkGrey) ${(toPosition)/(range)*100}%,
            var(--color-uiDarkGrey) 100%)`;
    }

    setPreciseIndicator(fakeInput, opposingFakeInput, indicator) {
        const config = indicator.dataset.jsPriceRangeIndicator.split('|');
        let updateIndicator = true;

        if (fakeInput.classList.contains('js-price-range-from-fake-input')) {
            if (parseFloat(fakeInput.value) > parseFloat(opposingFakeInput.value)) {
                updateIndicator = false;
            }
        } else {
            if (parseFloat(fakeInput.value) < parseFloat(opposingFakeInput.value)) {
                updateIndicator = false;
            }
        }

        if (updateIndicator) {
            indicator.innerHTML = `${config[0]}${Number(fakeInput.value).toFixed(2).replace('.', config[1]).replace(',', config[1])}`;
            indicator.style.left = `${(fakeInput.value / fakeInput.max) * 100}%`;
            indicator.style.transform = `translateX(calc(-${(fakeInput.value / fakeInput.max) * 50}% - 0.625rem))`;
            indicator.classList.remove('sr-only');
        }
    }
}

class FacetRemove extends HTMLElement {
    constructor() {
        super();
        const facetLink = this.querySelector('a');
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', this.closeFilter.bind(this));
        facetLink.addEventListener('keyup', (event) => {
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
        });
    }

    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
    }
}

export {
    FacetFilters,
    FacetFiltersForm,
    PriceRange,
    FacetRemove
}
